import React, {useCallback, useMemo, useState} from 'react';

// Components
import FaqItem from './FaqItem';
import FaqsModal from './FaqsModal';
import SearchBar from './SearchBar';

// Styles
import * as S from './Faqs.styles';

// Types
import {FaqData} from '@stencil/hotels-types/src/details-cg/types/static';
interface Faq {
  question: string;
  answer: string;
}

interface RenderQuestionsProps {
  faqs?: Faq[];
  openModal?: boolean;
  searchInput?: string;
  onToggleInput?: () => void;
  onChangeInput?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface FaqsProps {
  isPWA?: boolean;
  faqData?: FaqData;
  name?: string;
}

const FaqsUI: React.FC<FaqsProps> = ({isPWA = false, faqData = {} as FaqData, name = ''}) => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [openModal, setOpenModal] = useState<boolean>(false);

  const {extraItemText = '', faqs = [], title = ''} = faqData || ({} as FaqData);

  const heading = useMemo(() => `${title} about ${name}`, [title, name]);

  const handleToggle = useCallback((): void => {
    setOpenModal(prev => {
      if (prev) setSearchInput('');
      return !prev;
    });
  }, []);

  const updateSearchInput = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(event.target.value);
  }, []);

  const filteredFaqs = useMemo<Faq[]>(() => {
    if (!searchInput) return faqs;
    const lowerCaseSearchInput = searchInput.toLowerCase();
    return faqs.filter(
      ({question, answer}) =>
        question?.toLowerCase()?.includes(lowerCaseSearchInput) ||
        answer?.toLowerCase()?.includes(lowerCaseSearchInput),
    );
  }, [faqs, searchInput]);

  if (faqs.length === 0) return null;

  return (
    <S.MainWrapper $$isPWA={isPWA}>
      <S.FaqsHeaderWrapper $$isPWA={isPWA}>
        <h2>{heading}</h2>
      </S.FaqsHeaderWrapper>
      <S.QuestionAnswerContainer $$isPWA={isPWA}>
        <RenderQuestions onToggleInput={handleToggle} faqs={faqs} />
        <S.showAllQuestionsButton onClick={handleToggle}>{extraItemText}</S.showAllQuestionsButton>
        <FaqsModal isPWA={isPWA} heading={heading} isOpen={openModal} closeModal={handleToggle}>
          <RenderQuestions
            openModal={true}
            onChangeInput={updateSearchInput}
            faqs={filteredFaqs}
            searchInput={searchInput}
          />
        </FaqsModal>
      </S.QuestionAnswerContainer>
    </S.MainWrapper>
  );
};

const RenderQuestions = ({
  faqs = [],
  openModal = false,
  searchInput = '',
  onToggleInput,
  onChangeInput,
}: RenderQuestionsProps) => (
  <>
    <S.SearchBarWrapper>
      <SearchBar onToggleInput={onToggleInput} onChangeInput={onChangeInput} />
    </S.SearchBarWrapper>
    {faqs.map((faq, index) => (
      <FaqItem key={index} idx={index} faq={faq} openModal={openModal} searchInput={searchInput} />
    ))}
  </>
);

export default FaqsUI;
